import { Injectable, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';

/**
 * Thin wrapper around the posthog-js SDK.
 *
 * - Initialised once at app startup (root-provided singleton).
 * - Every `capture` call runs outside NgZone so PostHog network
 *   activity never triggers Angular change-detection cycles.
 * - All events follow the naming convention `checkout_app_<object>_<action>`.
 */
@Injectable({ providedIn: 'root' })
export class PosthogService {
  constructor(private readonly ngZone: NgZone) {
    this.initPostHog();
  }

  /* ------------------------------------------------------------------ */
  /*  Initialisation                                                     */
  /* ------------------------------------------------------------------ */

  private initPostHog(): void {
    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        debug: !environment.production,
        person_profiles: 'always',
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Generic capture (always injects `channel` super-property)          */
  /* ------------------------------------------------------------------ */

  capture(eventName: string, properties?: Record<string, unknown>): void {
    this.ngZone.runOutsideAngular(() => {
      posthog.capture(eventName, {
        channel: 'ie-payments',
        ...properties,
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Typed convenience methods                                          */
  /* ------------------------------------------------------------------ */

  /** Checkout modal opened and transaction loaded successfully. */
  trackOpenSuccess(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_open_success', properties);
  }

  /** Checkout modal failed to load (bad ref, auth failure, etc.). */
  trackOpenFailure(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_open_failure', properties);
  }

  /** User selected / initiated a payment method. */
  trackPaymentMethodInitiated(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_payment_method_initiated', properties);
  }

  /** Payment completed successfully. */
  trackPaymentMethodSuccessful(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_payment_method_successful', properties);
  }

  /** Payment failed. */
  trackPaymentMethodFailed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_payment_method_failed', properties);
  }

  /** Checkout modal / widget closed by the user. */
  trackClosed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_closed', properties);
  }

  /** Generic API error captured by the HTTP interceptor. */
  trackApiError(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_api_error', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Session events                                                     */
  /* ------------------------------------------------------------------ */

  /** Auth session handshake succeeded. */
  trackSessionAuthenticated(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_session_authenticated', properties);
  }

  /** Auth session handshake failed. */
  trackSessionAuthenticationFailed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_session_authentication_failed', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Navigation / page-view events                                      */
  /* ------------------------------------------------------------------ */

  /** User clicked "Retry" on the error screen. */
  trackRetryClicked(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_retry_clicked', properties);
  }

  /** Success page rendered after payment. */
  trackSuccessPageViewed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_success_page_viewed', properties);
  }

  /** Not-found page rendered (invalid / missing reference). */
  trackNotFoundPageViewed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_not_found_page_viewed', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Channel / payment-option events                                    */
  /* ------------------------------------------------------------------ */

  /** Switching payment channel failed. */
  trackChannelChangeFailed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_channel_change_failed', properties);
  }

  /** No payment options were returned by the API. */
  trackNoPaymentOptions(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_no_payment_options', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Card-channel events                                                */
  /* ------------------------------------------------------------------ */

  /** User clicked "Authenticate" to open 3-D Secure popup. */
  trackCard3dsInitiated(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_card_3ds_initiated', properties);
  }

  /** 3-D Secure popup was closed (by user or automatically). */
  trackCard3dsPopupClosed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_card_3ds_popup_closed', properties);
  }

  /** User submitted their 4-digit card PIN. */
  trackCardPinSubmitted(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_card_pin_submitted', properties);
  }

  /** User submitted OTP to authenticate card payment. */
  trackCardOtpSubmitted(): void {
    this.capture('checkout_app_card_otp_submitted');
  }

  /** User submitted phone number for card authentication. */
  trackCardPhoneSubmitted(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_card_phone_submitted', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Transfer events                                                    */
  /* ------------------------------------------------------------------ */

  /** User clicked "I've sent the money". */
  trackTransferConfirmClicked(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_transfer_confirm_clicked', properties);
  }

  /** User copied the account number to clipboard. */
  trackTransferAccountCopied(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_transfer_account_copied', properties);
  }

  /** User selected a bank from the dropdown (multi-bank). */
  trackTransferBankSelected(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_transfer_bank_selected', properties);
  }

  /** Virtual account expired before payment was made. */
  trackTransferAccountExpired(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_transfer_account_expired', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  USSD events                                                        */
  /* ------------------------------------------------------------------ */

  /** USSD shortcode fetched and displayed to user. */
  trackUssdShortcodeDisplayed(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_ussd_shortcode_displayed', properties);
  }

  /** User clicked "I've completed the payment" on USSD screen. */
  trackUssdConfirmClicked(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_ussd_confirm_clicked', properties);
  }

  /* ------------------------------------------------------------------ */
  /*  Wallet events                                                      */
  /* ------------------------------------------------------------------ */

  /** User clicked "Pay" on the wallet screen. */
  trackWalletPayClicked(properties?: Record<string, unknown>): void {
    this.capture('checkout_app_wallet_pay_clicked', properties);
  }
}
